import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
    Image as ImageIcon, 
    Play, 
    X, 
    ChevronLeft, 
    ChevronRight,
    Camera,
    Filter
} from 'lucide-react';

const galleryItems = [
    {
        id: 1,
        type: 'video',
        thumbnail: 'https://customer-assets.emergentagent.com/job_yash-thakur-band/artifacts/zrs4fm4l_6.jpeg',
        src: 'https://customer-assets.emergentagent.com/job_yash-thakur-band/artifacts/q2m5080j_Concert%20Video1.mp4',
        alt: 'Live Concert Performance - Full Energy',
        category: 'Concert',
    },
    {
        id: 2,
        type: 'image',
        src: 'https://customer-assets.emergentagent.com/job_yash-thakur-band/artifacts/zrs4fm4l_6.jpeg',
        alt: 'The Carnival - Live Performance with Pyrotechnics',
        category: 'Concert',
    },
    {
        id: 3,
        type: 'video',
        thumbnail: 'https://customer-assets.emergentagent.com/job_yash-thakur-band/artifacts/0tqq5lae_7.jpeg',
        src: 'https://customer-assets.emergentagent.com/job_yash-thakur-band/artifacts/e39qob32_Concert%20Video2.mp4',
        alt: 'Energetic Stage Performance',
        category: 'Concert',
    },
    {
        id: 4,
        type: 'image',
        src: 'https://customer-assets.emergentagent.com/job_yash-thakur-band/artifacts/0tqq5lae_7.jpeg',
        alt: 'Yash Singh Thakur - Passionate Performance',
        category: 'Concert',
    },
    {
        id: 5,
        type: 'image',
        src: 'https://customer-assets.emergentagent.com/job_yash-thakur-band/artifacts/wvv2ruft_16.jpeg',
        alt: 'Hawayein The Band - Full Team Photo',
        category: 'Backstage',
    },
    {
        id: 6,
        type: 'video',
        thumbnail: 'https://customer-assets.emergentagent.com/job_yash-thakur-band/artifacts/ae7t0urh_13.jpeg',
        src: 'https://customer-assets.emergentagent.com/job_yash-thakur-band/artifacts/oonk1qwj_Concert%20Video%203.mp4',
        alt: 'Wedding Performance Highlights',
        category: 'Wedding',
    },
    {
        id: 7,
        type: 'image',
        src: 'https://customer-assets.emergentagent.com/job_yash-thakur-band/artifacts/ae7t0urh_13.jpeg',
        alt: 'Yash - Elegant Wedding Performance',
        category: 'Wedding',
    },
    {
        id: 8,
        type: 'image',
        src: 'https://customer-assets.emergentagent.com/job_yash-thakur-band/artifacts/rz7ko0bw_14.jpeg',
        alt: 'Colorful Stage Performance',
        category: 'Wedding',
    },
    {
        id: 9,
        type: 'image',
        src: 'https://customer-assets.emergentagent.com/job_yash-thakur-band/artifacts/vzl0aowj_2.jpeg',
        alt: 'Pyrotechnics Show',
        category: 'Concert',
    },
    {
        id: 10,
        type: 'image',
        src: 'https://customer-assets.emergentagent.com/job_yash-thakur-band/artifacts/vco5639w_1.jpeg',
        alt: 'Yash with Green Fur Coat',
        category: 'Concert',
    },
];

const categories = ['All', 'Concert', 'Wedding', 'Corporate', 'Private Party', 'Backstage'];

const GalleryPage = () => {
    const [selectedItem, setSelectedItem] = useState(null);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [activeCategory, setActiveCategory] = useState('All');

    const filteredItems = activeCategory === 'All' 
        ? galleryItems 
        : galleryItems.filter(item => item.category === activeCategory);

    const openLightbox = (item, index) => {
        setSelectedItem(item);
        setCurrentIndex(index);
    };

    const closeLightbox = () => {
        setSelectedItem(null);
    };

    const navigateLightbox = (direction) => {
        const newIndex = direction === 'next' 
            ? (currentIndex + 1) % filteredItems.length 
            : (currentIndex - 1 + filteredItems.length) % filteredItems.length;
        setCurrentIndex(newIndex);
        setSelectedItem(filteredItems[newIndex]);
    };

    return (
        <main className="min-h-screen pt-24 pb-16 relative overflow-hidden">
            {/* Background Effects */}
            <div className="absolute top-1/4 left-0 w-96 h-96 bg-primary/10 rounded-full blur-[150px] pointer-events-none" />
            <div className="absolute bottom-1/4 right-0 w-80 h-80 bg-accent/10 rounded-full blur-[120px] pointer-events-none" />

            <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                {/* Page Header */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    className="text-center mb-12"
                >
                    <div className="inline-flex items-center gap-2 mb-4">
                        <Camera className="w-5 h-5 text-primary" />
                        <span className="text-sm font-body font-medium text-primary uppercase tracking-wider">
                            Gallery
                        </span>
                    </div>
                    <h1 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-foreground mb-4">
                        Our Visual Journey
                    </h1>
                    <p className="text-muted-foreground text-base sm:text-lg font-body max-w-2xl mx-auto">
                        Explore moments captured from our live performances, backstage preparations, and memorable events
                    </p>
                </motion.div>

                {/* Category Filters */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.1 }}
                    className="flex flex-wrap justify-center gap-2 mb-10"
                >
                    {categories.map((category) => (
                        <Button
                            key={category}
                            variant={activeCategory === category ? 'default' : 'outline'}
                            onClick={() => setActiveCategory(category)}
                            className={`rounded-full font-body ${
                                activeCategory === category 
                                    ? 'bg-primary text-foreground' 
                                    : 'border-border/50 text-muted-foreground hover:text-foreground hover:border-primary/50'
                            }`}
                        >
                            {category}
                        </Button>
                    ))}
                </motion.div>

                {/* Gallery Grid */}
                <motion.div
                    layout
                    className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6"
                >
                    <AnimatePresence mode="popLayout">
                        {filteredItems.map((item, index) => (
                            <motion.div
                                key={item.id}
                                layout
                                initial={{ opacity: 0, scale: 0.9 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.9 }}
                                transition={{ duration: 0.3 }}
                                className="relative group cursor-pointer rounded-2xl overflow-hidden aspect-[4/3]"
                                onClick={() => openLightbox(item, index)}
                            >
                                <img
                                    src={item.type === 'video' ? item.thumbnail : item.src}
                                    alt={item.alt}
                                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                                />
                                
                                {/* Overlay */}
                                <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-background/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                                
                                {/* Video Play Icon */}
                                {item.type === 'video' && (
                                    <div className="absolute inset-0 flex items-center justify-center">
                                        <div className="w-14 h-14 rounded-full bg-primary/90 flex items-center justify-center group-hover:scale-110 transition-transform duration-300 glow-primary">
                                            <Play className="w-6 h-6 text-foreground fill-foreground ml-1" />
                                        </div>
                                    </div>
                                )}

                                {/* Hover Content */}
                                <div className="absolute bottom-0 left-0 right-0 p-4 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                                    <Badge className="mb-2 bg-primary/20 text-primary border-none">
                                        {item.category}
                                    </Badge>
                                    <p className="text-sm text-foreground font-body">{item.alt}</p>
                                </div>

                                {/* Border Effect */}
                                <div className="absolute inset-0 border border-primary/0 group-hover:border-primary/40 rounded-2xl transition-colors duration-300" />
                            </motion.div>
                        ))}
                    </AnimatePresence>
                </motion.div>
            </div>

            {/* Lightbox Dialog */}
            <Dialog open={!!selectedItem} onOpenChange={closeLightbox}>
                <DialogContent className="max-w-4xl w-full bg-background/95 backdrop-blur-xl border-border/50 p-0 overflow-hidden">
                    <AnimatePresence mode="wait">
                        {selectedItem && (
                            <motion.div
                                key={selectedItem.id}
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                className="relative"
                            >
                                {/* Close Button */}
                                <button
                                    onClick={closeLightbox}
                                    className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-background/80 backdrop-blur-sm flex items-center justify-center text-foreground hover:bg-background transition-colors"
                                >
                                    <X className="w-5 h-5" />
                                </button>

                                {/* Navigation Arrows */}
                                <button
                                    onClick={() => navigateLightbox('prev')}
                                    className="absolute left-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-background/80 backdrop-blur-sm flex items-center justify-center text-foreground hover:bg-background transition-colors"
                                >
                                    <ChevronLeft className="w-5 h-5" />
                                </button>
                                <button
                                    onClick={() => navigateLightbox('next')}
                                    className="absolute right-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-background/80 backdrop-blur-sm flex items-center justify-center text-foreground hover:bg-background transition-colors"
                                >
                                    <ChevronRight className="w-5 h-5" />
                                </button>

                                {/* Content */}
                                <div className="aspect-video w-full">
                                    {selectedItem.type === 'video' ? (
                                        <video
                                            src={selectedItem.src}
                                            controls
                                            autoPlay
                                            className="w-full h-full bg-background"
                                        />
                                    ) : (
                                        <img
                                            src={selectedItem.src}
                                            alt={selectedItem.alt}
                                            className="w-full h-full object-contain bg-background"
                                        />
                                    )}
                                </div>

                                {/* Caption */}
                                <div className="p-4 bg-card/50">
                                    <Badge className="mb-2 bg-primary/20 text-primary border-none">
                                        {selectedItem.category}
                                    </Badge>
                                    <p className="text-foreground font-body">{selectedItem.alt}</p>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </DialogContent>
            </Dialog>
        </main>
    );
};

export default GalleryPage;
